// Risk Analytics Component
// Analytics and reporting for risk management

import React, { useState, useEffect } from 'react';
import { RiskAnalytics as RiskAnalyticsType } from '../../types/risk';
import { RiskService } from '../../services/riskService';
import { useLanguage } from '../../state/language/LanguageProvider';

export const RiskAnalytics: React.FC = () => {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState<RiskAnalyticsType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const riskService = new RiskService();

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const analyticsData = await riskService.getRiskAnalytics();
      setAnalytics(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('risk.analytics.errors.loadAnalytics'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className="text-[var(--color-secondary-text)]">{t('risk.analytics.noData')}</p>
      </div>
    );
  }

  return (
    <div className="risk-analytics">
      <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-6 mb-8">
        {/* Risk Distribution by Category */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">
              {t('risk.analytics.risksByCategory') || 'Risks by Category'}
            </h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-3">
              {(analytics?.risks_by_category || []).length === 0 ? (
                <div className="text-xs sm:text-sm text-[var(--color-secondary-text)]">{t('risk.analytics.noData') || 'No data'}</div>
              ) : (
                (analytics?.risks_by_category || []).map((row: { category_name: string | null; count: string }, idx: number) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">
                      {row.category_name || 'Uncategorized'}
                    </span>
                    <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{row.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Risk Distribution by Level */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.risksByLevel')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-3">
              {Object.entries(analytics?.risks_by_level || {}).map(([level, count]) => (
                <div key={level} className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{level}</span>
                  <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Distribution by Status */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.risksByStatus')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-3">
              {Object.entries(analytics?.risks_by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{status}</span>
                  <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Distribution by Severity */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.risksBySeverity')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-3">
              {Object.entries(analytics?.risks_by_severity || {}).map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{severity}</span>
                  <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6 mb-8">
        {/* Assignment Metrics */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.assignmentMetrics')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.totalAssigned')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.assignment_metrics?.total_assigned || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.accepted')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.assignment_metrics?.accepted || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.rejected')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.assignment_metrics?.rejected || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.pending')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.assignment_metrics?.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.avgAssignmentTime')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.assignment_metrics?.average_assignment_time || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Escalation Metrics */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.escalationMetrics')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.totalEscalations')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.escalation_metrics?.total_escalations || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.resolved')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.escalation_metrics?.resolved || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.pending')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.escalation_metrics?.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.avgResolutionTime')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.escalation_metrics?.average_resolution_time || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mitigation Metrics */}
        <div className="border border-[var(--color-border)] rounded-lg">
          <div className="p-2 sm:p-4 md:p-5 lg:p-6">
            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-2 sm:mb-4">{t('risk.analytics.mitigationMetrics')}</h3>
            <div className="space-y-1 sm:space-y-2 md:space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.totalMitigations')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.mitigation_metrics?.total_mitigations || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.completed')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.mitigation_metrics?.completed || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.inProgress')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.mitigation_metrics?.in_progress || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.overdue')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.mitigation_metrics?.overdue || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-[var(--color-secondary-text)] truncate min-w-0">{t('risk.analytics.completionRate')}</span>
                <span className="text-xs sm:text-sm text-[var(--color-surface-text)] flex-shrink-0 ml-1">{analytics?.mitigation_metrics?.completion_rate || 0}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
